
import React from 'react';
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  File, 
  FileImage, 
  FileText, 
  File as FileIcon, 
  Trash2, 
  ExternalLink,
  Loader2
} from "lucide-react";
import { JournalAttachment } from "@/types/journal";

interface AttachmentListProps {
  attachments: JournalAttachment[];
  onDelete: (id: string) => void;
}

const AttachmentList = ({ attachments, onDelete }: AttachmentListProps) => {
  const [loadingStates, setLoadingStates] = React.useState<Record<string, boolean>>({});

  const handleDelete = async (attachment: JournalAttachment) => {
    setLoadingStates(prev => ({ ...prev, [attachment.id]: true }));
    
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('journal_attachments')
        .remove([attachment.file_path]);
      
      if (storageError) {
        toast.error(`Error removing file: ${storageError.message}`);
        return;
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('journal_attachments')
        .delete()
        .eq('id', attachment.id);
      
      if (dbError) {
        toast.error(`Error deleting attachment record: ${dbError.message}`);
        return;
      }

      toast.success('Attachment deleted successfully');
      onDelete(attachment.id);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    } finally {
      setLoadingStates(prev => ({ ...prev, [attachment.id]: false }));
    }
  };

  const getFileUrl = async (filePath: string): Promise<string> => {
    const { data } = await supabase.storage
      .from('journal_attachments')
      .createSignedUrl(filePath, 60 * 5); // 5 minutes
    
    return data?.signedUrl || '';
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <FileImage className="h-5 w-5" />;
    } else if (mimeType === 'application/pdf') {
      return <FileIcon className="h-5 w-5" />;
    } else if (mimeType === 'text/plain') {
      return <FileText className="h-5 w-5" />;
    } else {
      return <File className="h-5 w-5" />;
    }
  };

  const handleView = async (attachment: JournalAttachment) => {
    try {
      const url = await getFileUrl(attachment.file_path);
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Failed to generate file URL');
      }
    } catch (error) {
      console.error('Error generating file URL:', error);
      toast.error('Failed to open file');
    }
  };

  if (!attachments.length) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Attached Files</h3>
      <ul className="space-y-2">
        {attachments.map((attachment) => (
          <li 
            key={attachment.id} 
            className="flex items-center justify-between border rounded-md p-3 bg-white"
          >
            <div className="flex items-center space-x-3">
              <div className="text-[#333B44]">
                {getFileIcon(attachment.mime_type)}
              </div>
              <div>
                <p className="text-sm font-medium truncate max-w-[180px] md:max-w-[300px]">
                  {attachment.filename}
                </p>
                <p className="text-xs text-muted-foreground">
                  {(attachment.file_size / 1024).toFixed(1)} KB
                </p>
              </div>
              {/* Removed validation status indicators */}
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => handleView(attachment)}
                className="h-8 w-8 p-0"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="sr-only">View</span>
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => handleDelete(attachment)}
                disabled={loadingStates[attachment.id]}
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
              >
                {loadingStates[attachment.id] ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span className="sr-only">Delete</span>
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AttachmentList;
